import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Card, CardContent, Typography, Grid, IconButton, Button,
  Box, TextField, FormControl, InputLabel, Select, MenuItem,
  Avatar, Divider, OutlinedInput, InputAdornment,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Star, StarBorder, Share } from '@mui/icons-material';
import DetailItem from './DetailItem';
import ActionButtons from './ActionButtons';
import DisponibilitePicker from './DisponibilitePicker';
import { countryToCurrency, CurrencySelect } from '../Recruteur/candidat/AddTalent/CurrencySelect';
import './MissionInfo.css';
import axios from 'axios';
import debounce from 'lodash.debounce'; 
import { secteurOptions as initialSecteurs } from '../commun/constants/options';
import DeleteIcon from '@mui/icons-material/Delete';

const modeOptions = ['Présentiel', 'À distance', 'Hybride'];
const urgenceOptions = ['Urgente', 'Normale'];

const MissionInfo = ({
  mission, editedMission, editable, onChange, onSave, onToggleEdit,
  onToggleStatus, isRecruteurView = false, favorites = [],
  toggleFavorite, setSelectedMission, showActionButtons = false,
  entrepriseLogo
}) => {
  const data = editable ? editedMission : mission;
  const inputRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [secteurOptions, setSecteurOptions] = useState(initialSecteurs);
  const [newSecteur, setNewSecteur] = useState('');
  const [showAddSecteurField, setShowAddSecteurField] = useState(false);
  const [lieuxTravailInput, setLieuxTravailInput] = useState(data.lieuxTravail || '');

  const [openConfirm, setOpenConfirm] = useState(false);

  const handleOpenConfirm = () => setOpenConfirm(true);
  const cancelConfirm = () => setOpenConfirm(false);

  const handleConfirm = () => {
    onToggleStatus();        
    setOpenConfirm(false);  
  };

  const fetchAutocomplete = async (input) => {
    if (!input || input.length < 2) return;
    setLoadingAutocomplete(true);
    try {
      const res = await axios.get('/api/autocomplete', { params: { input } });
      const suggestions = res.data.predictions.map(p => ({
        label: p.description,
        place_id: p.place_id
      }));
      setAutocompleteOptions(suggestions);
    } catch (err) {
      console.error("Erreur lors de l'autocomplétion :", err);
    } finally {
      setLoadingAutocomplete(false);
    }
  };  
  
  const debouncedFetchAutocomplete = useMemo(() => debounce(fetchAutocomplete, 300), []);
  
  useEffect(() => {
    return () => debouncedFetchAutocomplete.cancel(); 
  }, []);
  
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsMapReady(true);
    }
  }, []);

  useEffect(() => {
    if (isMapReady && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const fullAddress = place.formatted_address || place.name || '';
        onChange('lieuxTravail', fullAddress);

        const region = place.address_components?.find(comp =>
          comp.types.includes('administrative_area_level_1')
        )?.long_name;

        onChange('regionTravail', region || fullAddress);
      });
    }
  }, [isMapReady]);

    useEffect(() => {
      if (editable && editedMission?.embedding) {
        onChange('embedding', undefined); 
      }
    }, [editable, editedMission, onChange]);
    
    const handleAddSecteur = async () => {
      const trimmed = newSecteur.trim();
      if (!trimmed) return;
    
      if (secteurOptions.some(opt => opt.libelle.toLowerCase() === trimmed.toLowerCase())) {
        alert("Ce secteur existe déjà.");
        return;
      }
    
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/missions/secteur`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        });
    
        const data = await response.json();
    
        if (response.ok) {
          const newSecteurObj = {
            code: data.secteur?.code || data.secteur?.id || data.secteur?.name || data.secteur,
            libelle: data.secteur?.name || data.secteur?.libelle || trimmed,
          };
    
          setSecteurOptions(prev => [...prev, newSecteurObj]);
          onChange('secteur', newSecteurObj.code);
          setNewSecteur('');
          setShowAddSecteurField(false);
        } else {
          alert(data.error || 'Erreur lors de l’ajout du secteur.');
        }
      } catch (error) {
        console.error(error);
        alert("Erreur serveur lors de l’ajout du secteur.");
      }
    };
    
  return (
    <Card className="mission-card-modern">
      <CardContent>
        <Box className="mission-header">
          <Box className="mission-header-left">
            <Avatar
              variant="rounded"
              src={entrepriseLogo ? `${process.env.REACT_APP_API_BASE_URL}${entrepriseLogo}` : undefined}
              alt={data.entreprise?.NomEntreprise || "Entreprise"}
              className="logo-avatar"
            >
              {data.entreprise?.NomEntreprise?.[0]?.toUpperCase() || "E"}
            </Avatar>
            {editable ? (
              <TextField
                label="Intitulé de la mission"
                value={data.nomMission}
                onChange={(e) => onChange('nomMission', e.target.value)}
                className="editInput missionTitleInput"
                fullWidth
              />
            ) : (
              <Typography variant="h5" className="mission-title">
                {data.nomMission}
              </Typography>
            )}
          </Box>

          <Box className="mission-header-right">
          {!isRecruteurView && (
            <>
              <Button
                variant="outlined"
                className={`status-btn ${data.status === 'active' ? 'green' : 'red'}`}
                onClick={handleOpenConfirm} 
              >
                {data.status === 'active'
                  ? 'PUBLIÉE'
                  : data.status?.toUpperCase()}
              </Button>
            </>
          )}

          {isRecruteurView && (
            <>
              <IconButton onClick={() => toggleFavorite(data._id)}>
                {favorites.includes(data._id) ? <Star color="primary" /> : <StarBorder />}
              </IconButton>
              <IconButton onClick={() => setSelectedMission(data)}>
                <Share />
              </IconButton>
            </>
          )}

          {showActionButtons && (
            <ActionButtons
              isEditing={editable}
              onEdit={onToggleEdit}
              onSave={onSave}
              onCancel={onToggleEdit}
              showDelete={false}
              showDownload={false}
              showView={false}
            />
          )}

        </Box>
        </Box>

        <Divider className="mission-divider" />

        {editable ? (
          <TextField
            label="Contexte de la mission"
            variant="outlined"
            multiline
            minRows={3}
            fullWidth
            value={data.contexteMission}
            onChange={(e) => onChange('contexteMission', e.target.value)}
          />
        ) : (
          <DetailItem label="Contexte de la mission" value={data.contexteMission} />
        )}
        
        <div className="profilGrid">
          <div className="profilColumn">
              {editable ? (
                <>                               
                <Autocomplete
                  freeSolo
                  autoHighlight
                  clearOnBlur={false}
                  fullWidth
                  options={autocompleteOptions}
                  loading={loadingAutocomplete}
                  value={data.lieuxTravail}
                  inputValue={lieuxTravailInput}
                  onInputChange={(e, newInput) => {
                    setLieuxTravailInput(newInput);
                    onChange('lieuxTravail', newInput);
                    onChange('regionTravail', newInput);
                    debouncedFetchAutocomplete(newInput);
                  }}
                  onChange={async (e, value) => {
                    const description = typeof value === 'string' ? value : value?.label || '';
                    onChange('lieuxTravail', description);
                    onChange('regionTravail', description);
                  
                    const placeId = typeof value === 'string' ? null : value?.place_id;
                    if (placeId) {
                      try {
                        const res = await axios.get('/api/place-details', { params: { place_id: placeId } });
                        const components = res.data.result?.address_components || [];
                        const country = components.find(c => c.types.includes('country'))?.long_name;
                  
                        if (country) {
                          const currency = countryToCurrency[country] || 'EUR';
                          const normalized = country.toLowerCase();
                          const isMorocco = normalized.includes('maroc') || normalized.includes('morocco');
                          const defaultRem = isMorocco ? 5000 : 1000;
                  
                          onChange('devise', currency);
                          onChange('remuneration', defaultRem);
                        }
                      } catch (err) {
                        console.error('Erreur lors de la récupération du pays :', err);
                      }
                    }
                  }}
                  
                  noOptionsText={loadingAutocomplete ? 'Chargement…' : 'Aucun lieu trouvé'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lieu de travail"
                      inputRef={inputRef}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAutocomplete && <CircularProgress size={18} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                  <TextField
                    label="Mode de travail"
                    select
                    fullWidth
                    value={data.modeTravail || ''}
                    onChange={(e) => onChange('modeTravail', e.target.value)}
                  >
                    {modeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Secteur"
                    select
                    fullWidth
                    value={data.secteur || ""}
                    onChange={(e) => onChange("secteur", e.target.value)}
                  >
                    {secteurOptions.map((option) => (
                      <MenuItem key={option.code} value={option.libelle}>
                        {option.libelle}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Date limite"
                    type="date"
                    fullWidth
                    style={{ marginBottom: 16 }}
                    value={
                      data.dateLimite
                        ? new Date(data.dateLimite).toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      onChange('dateLimite', v ? new Date(v) : null);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              ) : (
                <>
                  <DetailItem label="Lieux de travail" value={data.lieuxTravail} />
                  <DetailItem label="Mode de travail" value={data.modeTravail} />
                  <DetailItem label="Secteur" value={data.secteur || 'N/A'} />
                  <DetailItem label="Date limite" value={
                    data.dateLimite
                      ? new Date(data.dateLimite).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : 'Non spécifiée'
                  } />
                </>
              )}
          </div>

          {/* RIGHT COLUMN */}
          <div className="profilColumn">
              {editable ? (
                <>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="mission-remuneration">Prime de la mission</InputLabel>
                    <OutlinedInput
                      id="mission-remuneration"
                      type="number"
                      value={data.remuneration ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        onChange('remuneration', value === '' ? null : Number(value));
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <CurrencySelect
                            value={data.devise || 'EUR'}
                            onChange={(newDevise) => onChange('devise', newDevise)}
                          />
                        </InputAdornment>
                      }
                      label="Prime de la mission"
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Priorité</InputLabel>
                    <Select
                      value={data.urgenceMission || ''}
                      onChange={(e) => onChange('urgenceMission', e.target.value)}
                      label="Priorité"
                    >
                      {urgenceOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DisponibilitePicker 
                    label="Date de démarrage"
                    value={
                      data.dateDebut === 'Immédiate'
                        ? 'Immédiate'
                        : data.dateDebut
                        ? new Date(data.dateDebut)
                        : null
                    }
                    onChange={(val) => {
                      if (val === 'Immédiate') {
                        onChange('dateDebut', 'Immédiate');
                      } else {
                        onChange('dateDebut', val?.toISOString?.() || null);
                      }
                    }}
                  />
                  <TextField
                    label="Profil(s) recherché(s)"
                    type="number"
                    value={data.placesDispo}
                    onChange={(e) => onChange('placesDispo', e.target.value)}
                    fullWidth
                  />

                </>
              ) : (
                <>
                  <DetailItem
                    label="Prime de la mission"
                    value={data.remuneration ? `${data.remuneration} ${data.devise || '€'}` : 'N/A'}
                  />
                  <DetailItem label="Priorité" value={data.urgenceMission} />
                  <DetailItem
                    label="Date de démarrage"
                    value={
                      data.dateDebut
                        ? (() => {
                            const d = new Date(data.dateDebut);
                            const today = new Date();
                            const isToday =
                              d.getDate() === today.getDate() &&
                              d.getMonth() === today.getMonth() &&
                              d.getFullYear() === today.getFullYear();

                            return isToday
                              ? 'Immédiate'
                              : d.toLocaleDateString('fr-FR');
                          })()
                        : 'Non spécifiée'
                    }
                  />
                  <DetailItem label="Nombre de profils" value={data.placesDispo} />
                </>
              )}
            </div>
          </div>
          <Box sx={{ mt: 3 }}>
            <DetailItem
              label="Document associé :"
              value={data.document?.name}
            />

            {editable ? (
              <>
                {data.document?.data ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="a"
                      href={`${process.env.REACT_APP_API_BASE_URL}${data.document.data}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📄 {data.document?.name || "Voir le document"}
                    </Button>

                    <Button
                      variant="text"
                      color="error"
                      onClick={() => onChange('document', null)}
                    >
                      <DeleteIcon /> 
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                    >
                      Ajouter un document
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            onChange('document', file);
                          }
                        }}
                      />
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                {data.document?.data ? (
                  <Button
                    variant="outlined"
                    component="a"
                    href={`${process.env.REACT_APP_API_BASE_URL}${data.document.data}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📄 {data.document?.name || "Voir le document"}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucun document associé
                  </Typography>
                )}
              </>
            )}
          </Box>


          <Dialog open={openConfirm} onClose={cancelConfirm}>
            <DialogTitle>Confirmer le changement de statut</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Êtes-vous sûr de vouloir {data.status === 'active' ? 'désactiver' : 'activer et publier'} cette mission ?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={cancelConfirm}>Annuler</Button>
              <Button onClick={handleConfirm} color="primary" variant="contained">
                Confirmer
              </Button>
            </DialogActions>
          </Dialog>
      </CardContent>
    </Card>
  );
};

export default MissionInfo;
